import React, { useCallback, useState } from "react";

import { AHSeparator, AHSwitch, Box, ListItem } from "arkh";

import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";

import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "../../../../common/navigationHeaderOptions";

import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { UserLoggedInNavigatorNavigationProp } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { TeamIdentifiersComponent } from "@/src/features/TeamIdentifiers/TeamIdentifiersComponent";
import { TeamIcon } from "arkh/src/cross-platform/__generated__/AHTeamIcon.graphql";
import { ScrollView } from "react-native";
import { ReceiverStoresSelector } from "../../WorkspaceEntity/ReceiverStoresSelector";
import { OrgAdminCreateTeamSelectTeamsQuery } from "./__generated__/OrgAdminCreateTeamSelectTeamsQuery.graphql";
import { teamColors } from "@/src/features/TeamIdentifiers/teamColors";
import { TopicSelector } from "../../WorkspaceEntity/TopicSelector";

type ParamList = {
  OrgTeamCreateScreen: object;
  OrgTeamCreateSelectReceivers: {
    icon: TeamIcon;
    color: string;
    name: string;
    isPrivate: boolean;
  };
};

const Stack = createNativeStackNavigator<ParamList>();

export const OrgTeamCreate = withWebModal(
  () => {
    const intl_tokens = useIntlTokens();

    const getOptions = useNavigationHeaderOptions();
    return (
      <Stack.Navigator>
        <Stack.Screen
          name="OrgTeamCreateScreen"
          component={OrgTeamCreateScreen}
          options={({ navigation }) =>
            getOptions({
              type: "CREATE-INIT",
              title: intl_tokens.CreateTeam,
              onCancel: () => {
                navigation.goBack();
              },
            })
          }
        />
        <Stack.Screen
          name="OrgTeamCreateSelectReceivers"
          component={OrgTeamCreateSelectReceivers}
        />
      </Stack.Navigator>
    );
  },
  true,
  {
    height: 980,
  }
);

type OrgTeamCreateSelectTeamsProps = {
  route: RouteProp<ParamList, "OrgTeamCreateSelectReceivers">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"OrgAdminCreateTeam">,
    NativeStackNavigationProp<ParamList, "OrgTeamCreateSelectReceivers">
  >;
};
function OrgTeamCreateSelectReceivers(props: OrgTeamCreateSelectTeamsProps) {
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [currentTopicId, setCurrentTopicId] = useState<string | undefined>(
    undefined
  );

  const data = useLazyLoadQuery<OrgAdminCreateTeamSelectTeamsQuery>(
    graphql`
      query OrgAdminCreateTeamSelectTeamsQuery($topicId: ID) {
        organisation @required(action: THROW) {
          id
          rootWorkspace {
            potentialReceiverStores(topicId: $topicId) {
              store {
                id
              }
              ...ReceiverStoresSelector_receiverStores
            }
          }
          topics {
            id
            isPrivate
            ...TopicSelector_orgTopic
          }
        }
      }
    `,
    {
      topicId: currentTopicId,
    }
  );
  const intl_tokens = useIntlTokens();

  const [createTeamMutation, createTeamMutationLoading] = useMutation(graphql`
    mutation OrgAdminCreateTeamMutation(
      $storeIds: [ID!]!
      $teamName: String!
      $color: String!
      $icon: TeamIcon!
      $topicId: ID
      $isPrivate: Boolean!
    ) {
      organisation {
        createTeams(
          storeIds: $storeIds
          teamName: $teamName
          color: $color
          icon: $icon
          topicId: $topicId
          isPrivate: $isPrivate
        ) {
          id
          teams {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  `);

  const isTopicPrivate = data.organisation.topics?.find(
    (t) => t.id === currentTopicId
  )?.isPrivate;

  const filteredSelectedStoreIds = selectedStoreIds.filter((id) =>
    data.organisation.rootWorkspace.potentialReceiverStores.some(
      (s) => s.store.id === id
    )
  );
  const onSave = useCallback(() => {
    createTeamMutation({
      variables: {
        storeIds: filteredSelectedStoreIds,
        topicId: currentTopicId,

        teamName: props.route.params.name,
        color: props.route.params.color,
        icon: props.route.params.icon,
        isPrivate: props.route.params.isPrivate ?? false,
      },
      onCompleted: (data) => {
        props.navigation.popToTop();
        props.navigation.goBack();
      },
    });
  }, [
    createTeamMutation,
    filteredSelectedStoreIds,
    currentTopicId,
    props.route.params.name,
    props.route.params.color,
    props.route.params.icon,
    props.route.params.isPrivate,
    props.navigation,
  ]);

  useNavigationHeader({
    type: "CREATE",
    title: intl_tokens.CreateTeam,
    createButtonLabel: intl_tokens.Create,
    defaultContentStyle: true,
    onCancel: () => {
      props.navigation.goBack();
    },
    disabled: false,

    onSave: onSave,

    saveLoading: createTeamMutationLoading,
  });

  return (
    <>
      <TopicSelector
        topics={
          data.organisation.topics?.filter((t) =>
            props.route.params.isPrivate ? true : !t.isPrivate
          ) ?? []
        }
        currentTopicId={currentTopicId}
        onTopicSelect={setCurrentTopicId}
      />

      <ReceiverStoresSelector
        selectedStoreIds={filteredSelectedStoreIds}
        stores={data.organisation.rootWorkspace.potentialReceiverStores}
        isTopicPrivate={isTopicPrivate ?? false}
        onStoreSetSelect={(newSelectedStoreIds) => {
          setSelectedStoreIds(newSelectedStoreIds);
        }}
      />
    </>
  );
}

type OrgTeamCreateScreenProps = {
  route: RouteProp<ParamList, "OrgTeamCreateScreen">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"OrgAdminCreateTeam">,
    NativeStackNavigationProp<ParamList, "OrgTeamCreateScreen">
  >;
};

function OrgTeamCreateScreen(props: OrgTeamCreateScreenProps) {
  const intl_tokens = useIntlTokens();

  const [color, setColor] = useState<string>(teamColors[0]);
  const [icon, setIcon] = useState<TeamIcon>("default");
  const [name, setName] = useState<string>("");
  const [isPrivate, setIsPrivate] = useState<boolean>(false);

  useNavigationHeader({
    type: "CREATE",
    title: intl_tokens.CreateTeam,
    createButtonLabel: intl_tokens.Next,
    defaultContentStyle: true,
    onCancel: () => {
      props.navigation.goBack();
    },
    disabled: name.length === 0,

    onSave: () => {
      props.navigation.navigateDeprecated("OrgTeamCreateSelectReceivers", {
        icon,
        color,
        name,
        isPrivate,
      });
    },

    saveLoading: false,
  });

  return (
    <ScrollView>
      <Box space="medium">
        <Box
          cornerRadius="large"
          style={{ overflow: "hidden", borderWidth: 1 }}
          borderColor="border-primary"
        >
          <TeamIdentifiersComponent
            color={color}
            icon={icon}
            name={name}
            usedColors={[]}
            usedIcons={[]}
            setColor={setColor}
            setIcon={setIcon}
            setName={setName}
            mode="create"
            isPrivate={false}
            LockOption={
              <>
                <AHSeparator />
                <ListItem
                  right={
                    <AHSwitch value={isPrivate} onValueChange={setIsPrivate} />
                  }
                >
                  <ListItem.Title>{intl_tokens.LockedTeam}</ListItem.Title>
                </ListItem>
              </>
            }
          />
        </Box>
      </Box>
    </ScrollView>
  );
}
